import React, { ReactNode, useMemo } from 'react';

import { BsArrowDown, BsArrowUp, BsPlusLg } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { UiComponent } from 'components';
import { QuickMenuQuery } from 'queries';
import { useIsMobile } from 'hooks';
import useModal from 'hooks/useModal';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { TbDeviceMobile } from 'react-icons/tb';
import {
  header,
  layout,
  previewCard,
  cardHead,
  phone,
  phoneScreen,
  listCard,
} from 'styles/preview';
import * as M from 'styles/mobile';
import { tableWrap } from 'styles/table';
import { IQuickMenu } from 'types';
import Form from '../Form';
import Preview from '../Preview';
import * as ManageStyle from './style';

const COLS: string[] = [
  '순서',
  '아이콘',
  '타이틀',
  '경로',
  '노출 대상',
  '노출',
  '관리',
];

const exposureLabel = ({ exposureType, userGroups }: IQuickMenu) =>
  exposureType === 'USER_GROUP'
    ? userGroups.map(({ name }) => name).join(', ') || '그룹 없음'
    : '전체';

type RowType = IQuickMenu & { row: number };

function Manage() {
  const isMobile = useIsMobile();
  const { handleModal, handleConfirm } = useModal();
  const { data = [] } = QuickMenuQuery.useGetAll();
  const { mutateAsync: update } = QuickMenuQuery.usePut();
  const { mutateAsync: remove } = QuickMenuQuery.useDelete();
  const { mutate: putOrder, isPending: isOrdering } =
    QuickMenuQuery.usePutOrder();

  const openForm = (quickMenu?: IQuickMenu) =>
    handleModal(
      quickMenu ? '퀵메뉴 수정' : '퀵메뉴 추가',
      <Form quickMenu={quickMenu} />,
      <></>,
    );

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (isOrdering || target < 0 || target >= data.length) return;

    const ids = data.map((q) => q.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    putOrder(ids, { onError: () => toast.error(DEFAULT_ERROR_MESSAGE) });
  };

  const toggleActive = async (quickMenu: IQuickMenu) => {
    try {
      await update({ id: quickMenu.id, isActive: !quickMenu.isActive });
      toast.success(
        quickMenu.isActive ? '비활성화되었습니다.' : '활성화되었습니다.',
      );
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleDelete = (quickMenu: IQuickMenu) =>
    handleConfirm({
      confirmContent: `'${quickMenu.title}' 퀵메뉴를 삭제하시겠습니까?`,
      yesTitle: '삭제',
      confirmNext: async () => {
        try {
          await remove(quickMenu.id);
          toast.success('삭제되었습니다.');
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        }
      },
    });

  const renderer: Record<(typeof COLS)[number], (data: RowType) => ReactNode> =
    useMemo(
      () => ({
        순서: ({ row }: RowType) => (
          <div css={ManageStyle.order}>
            <button
              type="button"
              disabled={row === 0 || isOrdering}
              onClick={() => move(row, -1)}
            >
              <BsArrowUp />
            </button>
            <button
              type="button"
              disabled={row === data.length - 1 || isOrdering}
              onClick={() => move(row, 1)}
            >
              <BsArrowDown />
            </button>
          </div>
        ),
        아이콘: ({ iconUrl, title }: RowType) => (
          <img css={ManageStyle.icon} src={iconUrl} alt={title} />
        ),
        타이틀: ({ title }: RowType) => <p>{title}</p>,
        경로: ({ route }: RowType) => <p>{route.name}</p>,
        '노출 대상': (quickMenu: RowType) => <p>{exposureLabel(quickMenu)}</p>,
        노출: ({ isActive }: RowType) => (
          <p css={ManageStyle.status(isActive)}>
            {isActive ? '활성' : '비활성'}
          </p>
        ),
        관리: (quickMenu: RowType) => (
          <div css={ManageStyle.actions}>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => openForm(quickMenu)}
            >
              수정
            </UiComponent.Button>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => toggleActive(quickMenu)}
            >
              {quickMenu.isActive ? '비활성화' : '활성화'}
            </UiComponent.Button>
            <UiComponent.Button
              variant="border"
              color="error"
              onClick={() => handleDelete(quickMenu)}
            >
              삭제
            </UiComponent.Button>
          </div>
        ),
      }),
      [data, isOrdering],
    );

  const rows = data.map((q, row) => ({ ...q, row, rowKey: q.id }));

  const activeCount = data.filter((q) => q.isActive).length;

  return (
    <>
      <div css={header}>
        <UiComponent.PageTitle>퀵메뉴 관리</UiComponent.PageTitle>
        {!isMobile && (
          <UiComponent.Button
            color="primary"
            variant="contained"
            onClick={() => openForm()}
            css={{ height: '40px' }}
          >
            <BsPlusLg size={14} />
            퀵메뉴 추가
          </UiComponent.Button>
        )}
      </div>

      <div css={layout}>
        {!isMobile && (
          <section css={previewCard}>
            <div css={cardHead}>
              <h4>
                <TbDeviceMobile size={18} />앱 미리보기
              </h4>
              <p>MY 화면 · 활성 메뉴만 노출</p>
            </div>
            <div css={phone}>
              <div css={phoneScreen}>
                <Preview data={data} />
              </div>
            </div>
          </section>
        )}

        <section css={listCard}>
          <div css={cardHead}>
            <h4>
              퀵메뉴 <small>{`${activeCount} / ${data.length}`}</small>
            </h4>
            <p>순서 버튼으로 노출 순서를 바꿀 수 있어요</p>
          </div>
          {isMobile ? (
            <div>
              {data.map((quickMenu, row) => (
                <div key={quickMenu.id} css={ManageStyle.mobileRow}>
                  <div css={ManageStyle.mobileOrder}>
                    <button
                      type="button"
                      aria-label="위로"
                      disabled={row === 0 || isOrdering}
                      onClick={() => move(row, -1)}
                    >
                      <BsArrowUp />
                    </button>
                    <button
                      type="button"
                      aria-label="아래로"
                      disabled={row === data.length - 1 || isOrdering}
                      onClick={() => move(row, 1)}
                    >
                      <BsArrowDown />
                    </button>
                  </div>
                  <img
                    css={ManageStyle.mobileIcon}
                    src={quickMenu.iconUrl}
                    alt={quickMenu.title}
                  />
                  <div css={ManageStyle.mobileInfo}>
                    <p>{quickMenu.title}</p>
                    {quickMenu.exposureType === 'USER_GROUP' && (
                      <small>{exposureLabel(quickMenu)}</small>
                    )}
                    <span css={ManageStyle.status(quickMenu.isActive)}>
                      {quickMenu.isActive ? '활성' : '비활성'}
                    </span>
                  </div>
                  <UiComponent.Button
                    variant="border"
                    color={quickMenu.isActive ? 'grey' : 'primary'}
                    css={ManageStyle.mobileAction}
                    onClick={() => toggleActive(quickMenu)}
                  >
                    {quickMenu.isActive ? '비활성화' : '활성화'}
                  </UiComponent.Button>
                </div>
              ))}
              {data.length === 0 && (
                <p css={M.empty}>등록된 퀵메뉴가 없습니다</p>
              )}
            </div>
          ) : (
            <div css={[tableWrap, { marginTop: 0 }]}>
              <UiComponent.Table
                cols={COLS}
                renderer={renderer}
                data={rows}
                empty="등록된 퀵메뉴가 없습니다"
              />
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default Manage;
